import React, { useMemo, memo } from 'react'
import PropTypes from 'prop-types'
import { Box, Typography, Tooltip, useTheme } from '@mui/material'
import moment from 'moment'
import GridBackground from './GridBackground'
import { useStatusColors } from '../hooks/useStatusColors'
import { colorForOf } from '../utils/helpers'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import { systemValueLabel } from '../../../../generic/systemValues/systemValueLabel'
import { useDateFormatter } from '../../../../generic/hooks/useDateFormatter'

/**
 * Specialized Timeline for Manufacturing Orders (OF) with lanes and sublanes.
 * @param {object} props
 * @param {string} props.label - Row label.
 * @param {Array} props.segments - List of OF segments.
 * @param {moment.Moment} props.windowStart - Start of the time window.
 * @param {moment.Moment} props.windowEnd - End of the time window.
 */
const OfTimeline = ({ label, segments, windowStart, windowEnd }) => {
  const theme = useTheme()
  const statusColors = useStatusColors()
  const windowMs = windowEnd.diff(windowStart)
  const windowStartMs = windowStart.valueOf()
  const windowEndMs = windowEnd.valueOf()
  const nowMs = moment().valueOf()
  const { formatDateTime } = useDateFormatter()
  const nowPct = ((nowMs - windowStartMs) / windowMs) * 100
  const { t_ } = useSmartTranslation()

  // Calculate OF groups and layout
  const { ofGroups, laneTops, totalHeight } = useMemo(() => {
    // Normalize and clip segments
    const normalized = (segments || [])
      .map(seg => {
        const of = seg.fabricationOrder || seg.of || { id: seg.ofId || seg.of_id || seg.label, code: seg.ofCode || seg.label, name: seg.ofName || seg.label }
        const job = seg.jobReference || seg.job || { id: seg.jobId || seg.job_id || seg.label, code: seg.jobCode || seg.label, name: seg.jobName || seg.label }
        const startMs = Date.parse(seg.start)
        const originalEndMs = seg.end ? Date.parse(seg.end) : nowMs
        const s = Math.max(startMs, windowStartMs)
        const e = Math.min(originalEndMs, windowEndMs)
        if (!(isFinite(s) && isFinite(e) && e > s)) return null
        return {
          of,
          job,
          status: seg.status,
          statusType: seg.statusType,
          startMs: s,
          endMs: e
        }
      })
      .filter(Boolean)

    // Group by OF
    const groupsByOf = new Map()
    normalized.forEach(seg => {
      const key = seg.of?.id ?? seg.of?.code ?? 'unknown'
      if (!groupsByOf.has(key)) {
        groupsByOf.set(key, { of: seg.of, segments: [] })
      }
      groupsByOf.get(key).segments.push(seg)
    })

    const ofGroups = Array.from(groupsByOf.values()).map(g => {
      const ofStart = Math.min(...g.segments.map(s => s.startMs))
      const ofEnd = Math.max(...g.segments.map(s => s.endMs))
      return { ...g, ofStart, ofEnd }
    })

    // Assign lanes (interval partitioning)
    ofGroups.sort((a, b) => a.ofStart - b.ofStart)
    const lanesEnd = []
    ofGroups.forEach(g => {
      let laneIndex = lanesEnd.findIndex(end => g.ofStart >= end)
      if (laneIndex === -1) {
        laneIndex = lanesEnd.length
        lanesEnd.push(g.ofEnd)
      } else {
        lanesEnd[laneIndex] = g.ofEnd
      }
      g.lane = laneIndex
    })

    // Assign sublanes and calculate positions
    ofGroups.forEach(g => {
      g.segments.sort((a, b) => a.startMs - b.startMs)
      g.segments.forEach(s => {
        s.leftPct = ((s.startMs - windowStartMs) / windowMs) * 100
        s.widthPct = ((s.endMs - s.startMs) / windowMs) * 100
        s.color = statusColors[s.status?.code] ?? colorForOf(g.of?.id ?? g.of?.code) ?? theme.palette.grey[400]
      })
      const jobCodes = Array.from(new Set(g.segments.map(s => s.job?.code || s.job?.name).filter(Boolean)))
      g.jobsList = jobCodes
      g.subLaneCount = 1
    })

    // Heights
    const laneGap = 8
    const subLaneHeight = 28
    const subLaneGap = 4
    const laneCount = lanesEnd.length
    const laneRows = Array.from({ length: laneCount }, (_, i) =>
      Math.max(...ofGroups.filter(g => g.lane === i).map(g => g.subLaneCount || 0), 0) || 1
    )
    const laneHeights = laneRows.map(rows => rows * (subLaneHeight + subLaneGap) - subLaneGap)
    const laneTops = laneHeights.map((_, i) => {
      let sum = 0
      for (let j = 0; j < i; j++) {
        sum += laneHeights[j] + laneGap
      }
      return sum
    })
    const totalHeight = laneCount === 0 ? subLaneHeight : laneTops[laneCount - 1] + laneHeights[laneCount - 1]

    return { ofGroups, laneTops, totalHeight }
  }, [segments, windowStartMs, windowEndMs, windowMs, nowMs, statusColors, theme])

  const subLaneHeight = 28

  const moreThan24Hours = windowMs > 24 * 60 * 60 * 1000

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
      <Typography
        variant='caption' sx={{
          width: 120,
          minWidth: 120,
          textAlign: 'right',
          pr: 2,
          color: 'text.secondary',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontSize: '0.7rem',
          pt: 0.5
        }}
      >
        {label}
      </Typography>
      <Box sx={{
        position: 'relative',
        flexGrow: 1,
        bgcolor: theme.palette.background.paper,
        borderRadius: 1,
        border: `1px solid ${theme.palette.divider}`,
        height: Math.max(totalHeight, 40),
        overflow: 'hidden',
        boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)'
      }}
      >
        <GridBackground windowStart={windowStart} windowEnd={windowEnd} />

        {/* Render each OF group */}
        {ofGroups.map((g, gi) => {
          const laneTop = laneTops[g.lane] || 0
          return (g.segments || []).map((s, si) => {
            const top = laneTop
            const title = (
              <Box sx={{ p: 1 }}>
                <Typography variant='subtitle2' sx={{ fontWeight: 700, mb: 0.5, borderBottom: `1px solid ${theme.palette.divider}`, pb: 0.5 }}>
                  {t_('OF')} {g.of?.code || g.of?.name || 'N/A'}
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 1, rowGap: 0.5 }}>
                  <Typography variant='caption' color='text.secondary'>{t_('Job')}:</Typography>
                  <Typography variant='caption' fontWeight='500'>{s.job?.code || s.job?.name || 'N/A'}</Typography>

                  <Typography variant='caption' color='text.secondary'>{t_('Time')}:</Typography>
                  <Typography variant='caption' fontWeight='500'>{moreThan24Hours ? formatDateTime(moment(s.startMs)) : moment(s.startMs).format('HH:mm')} - {moreThan24Hours ? formatDateTime(moment(s.endMs)) : moment(s.endMs).format('HH:mm')}</Typography>

                  <Typography variant='caption' color='text.secondary'>{t_('Duration')}:</Typography>
                  <Typography variant='caption' fontWeight='500'>{moment.duration(s.endMs - s.startMs).humanize()}</Typography>

                  {s.status?.code && (
                    <>
                      <Typography variant='caption' color='text.secondary'>{t_('Status')}:</Typography>
                      <Box
                        component='span' sx={{
                          bgcolor: s.color,
                          color: theme.palette.getContrastText(s.color),
                          px: 0.5,
                          borderRadius: 0.5,
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          display: 'inline-block'
                        }}
                      >
                        {systemValueLabel(t_, 'status', s.status)}
                      </Box>
                    </>
                  )}
                </Box>
                {g.jobsList && g.jobsList.length > 0 && (
                  <Box sx={{ mt: 1, pt: 0.5, borderTop: `1px solid ${theme.palette.divider}` }}>
                    <Typography variant='caption' color='text.secondary' display='block' mb={0.5}>{t_('All Jobs in this OF')}:</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {g.jobsList.map(job => (
                        <Box
                          key={job} component='span' sx={{
                            bgcolor: theme.palette.action.selected,
                            px: 0.75,
                            py: 0.25,
                            borderRadius: 1,
                            fontSize: '0.65rem'
                          }}
                        >
                          {job}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )
            return (
              <Tooltip
                key={`${gi}-${si}`}
                title={title}
                arrow
                placement='top'
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: 'background.paper',
                      color: 'text.primary',
                      border: `1px solid ${theme.palette.divider}`,
                      boxShadow: theme.shadows[4],
                      p: 0,
                      borderRadius: 1,
                      maxWidth: 320
                    }
                  }
                }}
              >
                <Box
                  tabIndex={0}
                  role='button'
                  aria-label={`OF ${g.of?.code}, Job ${s.job?.code}, ${t_('Status')} : ${s.status?.code || t_('Unknown Status')}`}
                  sx={{
                    position: 'absolute',
                    top,
                    left: `${s.leftPct}%`,
                    width: `${s.widthPct}%`,
                    height: subLaneHeight,
                    bgcolor: s.color,
                    borderRight: '1px solid rgba(255,255,255,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    pl: 0.75,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    color: theme.palette.getContrastText(s.color),
                    cursor: 'pointer',
                    borderRadius: 0.5,
                    boxShadow: 1,
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    outline: 'none',
                    '&:hover, &:focus': {
                      opacity: 1,
                      zIndex: 10,
                      boxShadow: 4,
                      transform: 'translateY(-1px) scaleY(1.05)'
                    }
                  }}
                >
                  <Typography variant='caption' sx={{ fontWeight: 700, fontSize: '0.65rem', textOverflow: 'ellipsis', overflow: 'hidden', letterSpacing: '0.02em' }}>
                    {g.of?.code || g.of?.name}
                  </Typography>
                </Box>
              </Tooltip>
            )
          })
        })}

        {/* Vertical "Now" Line */}
        {nowPct >= 0 && nowPct <= 100 && (
          <Box
            sx={{
              position: 'absolute',
              left: `${nowPct}%`,
              top: 0,
              bottom: 0,
              width: '2px',
              bgcolor: theme.palette.error.main,
              zIndex: 20,
              pointerEvents: 'none',
              boxShadow: `0 0 6px ${theme.palette.error.main}`,
              '&::after': {
                content: '""',
                position: 'absolute',
                top: -2,
                left: -3,
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: theme.palette.error.main,
                boxShadow: `0 0 4px ${theme.palette.error.main}`
              }
            }}
          />
        )}
      </Box>
    </Box>
  )
}

OfTimeline.propTypes = {
  label: PropTypes.string.isRequired,
  segments: PropTypes.array,
  windowStart: PropTypes.object.isRequired,
  windowEnd: PropTypes.object.isRequired
}

export default memo(OfTimeline)
